import { Button, Center, Group, Text } from '@mantine/core';
import { IconArrowRight } from '@tabler/icons-react';
import { useGetDebtorTimeline } from 'api';
import type { CleanActivity } from 'api/models';
import { useDebtor } from 'app/Private/Clients/Detail/client-atom';
import { Panel } from 'components/Panel';
import { i18nKeys } from 'locales/index';
import { useTranslation } from 'locales/logic';
import type { PropsWithChildren } from 'react';
import { useHistory } from 'react-router';
import { isEmpty } from 'remeda';
import { ActivityItem } from '../../ActivitiesTab/ActivityItem';
import { ActivityItemSkeleton } from '../../ActivitiesTab/ActivityItemSkeleton';

export const ActivityInfoPanel = () => {
  const { t } = useTranslation();
  const debtor = useDebtor();

  const {
    data: debtorTimelineResponse,
    isLoading,
    isError,
  } = useGetDebtorTimeline(debtor.id, { page_limit: 5, page: 1, types: 'activity' });

  if (isLoading && !debtorTimelineResponse) {
    return (
      <BasePanel>
        <ActivityItemSkeleton />
        <ActivityItemSkeleton />
        <ActivityItemSkeleton />
      </BasePanel>
    );
  }

  if (isError || !debtorTimelineResponse) {
    return (
      <BasePanel>
        <Group align="start" mt="space6">
          <Text c="dimmed">{t(i18nKeys.CLIENT.GENERAL_INFO_TAB.COULD_NOT_FETCH)}</Text>
        </Group>
      </BasePanel>
    );
  }

  const { data } = debtorTimelineResponse;

  if (isEmpty(data)) {
    return (
      <BasePanel>
        <Center h="100%">
          <Text fs="italic" c="dimmed">
            {t(i18nKeys.CLIENT.GENERAL_INFO_TAB.NO_ACTIVITY)}
          </Text>
        </Center>
      </BasePanel>
    );
  }

  return (
    <BasePanel>
      {data.map(activity => (
        <ActivityItem key={activity.id} activity={activity as CleanActivity} />
      ))}
    </BasePanel>
  );
};

const BasePanel = ({ children }: PropsWithChildren) => {
  const history = useHistory();
  const { t } = useTranslation();
  const handleTabChange = () => history.replace(`#activities`);

  return (
    <Panel
      withBorder
      flushContent
      title={t(i18nKeys.CLIENT.GENERAL_INFO_TAB.PANEL_TITLE.ACTIVITY)}
      h="100%"
      topRight={
        <Button
          onClick={handleTabChange}
          size="compact-sm"
          variant="default"
          rightSection={<IconArrowRight size={18} />}
        >
          {t(i18nKeys.CLIENT.GENERAL_INFO_TAB.ACTIONS.SEE_ALL_ACTIVITY)}
        </Button>
      }
    >
      {children}
    </Panel>
  );
};
